import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "@/App";
import { AdminLayout } from "./Dashboard";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Zap, Plus, Loader2, Trash2, Edit2, Users, Clock,
    Radio, Trophy, ToggleRight, AlertTriangle, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";


const AutoSms = () => {
    const { token } = useAuth();
    const authH = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const [settings, setSettings] = useState(null);
    const [rules, setRules] = useState([]);
    const [cats, setCats] = useState({ audience_categories: [], event_types: [] });
    const [log, setLog] = useState([]);
    const [loading, setLoading] = useState(true);

    const emptyRule = {
        name: "", enabled: true, quota_per_match: 8,
        event_types: [], audience_categories: [], teams: [], competitions: [],
        customer_base_ids: [], recent_days: 30, min_transactions: 1,
    };
    const [editing, setEditing] = useState(null);
    const [preview, setPreview] = useState({ home: "", away: "", count: null, busy: false });

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [s, r, c, l] = await Promise.all([
                axios.get(`${API}/admin/auto-sms/settings`, { headers: authH }),
                axios.get(`${API}/admin/auto-sms/rules`, { headers: authH }),
                axios.get(`${API}/admin/auto-sms/categories`, { headers: authH }),
                axios.get(`${API}/admin/auto-sms/log`, { headers: authH, params: { days: 7 } }),
            ]);
            setSettings(s.data);
            setRules(r.data?.rules || []);
            setCats(c.data);
            setLog(l.data?.log || []);
        } catch (e) {
            toast.error("Load failed", { description: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, [authH]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const flipGlobal = async (val) => {
        try {
            const r = await axios.put(`${API}/admin/auto-sms/settings`, { enabled: val }, { headers: authH });
            setSettings(r.data);
            toast.success(val ? "Auto-SMS enabled" : "Auto-SMS paused");
        } catch (e) { toast.error("Toggle failed", { description: e?.response?.data?.detail }); }
    };

    const changeDefaultQuota = async (val) => {
        const q = Math.max(1, Math.min(100, Number(val) || 8));
        try {
            const r = await axios.put(`${API}/admin/auto-sms/settings`, { default_quota_per_match: q }, { headers: authH });
            setSettings(r.data);
        } catch (e) { toast.error("Quota save failed", { description: e?.response?.data?.detail }); }
    };

    const toggleRule = async (rid) => {
        try {
            const r = await axios.post(`${API}/admin/auto-sms/rules/${rid}/toggle`, {}, { headers: authH });
            setRules((rs) => rs.map((x) => (x.id === rid ? { ...x, enabled: r.data.enabled } : x)));
        } catch (e) { toast.error("Toggle failed", { description: e?.response?.data?.detail }); }
    };

    const deleteRule = async (rid) => {
        if (!window.confirm("Delete this rule?")) return;
        try {
            await axios.delete(`${API}/admin/auto-sms/rules/${rid}`, { headers: authH });
            setRules((rs) => rs.filter((x) => x.id !== rid));
            toast.success("Rule deleted");
        } catch (e) { toast.error("Delete failed", { description: e?.response?.data?.detail }); }
    };

    const saveRule = async () => {
        if (!editing) return;
        const payload = { ...editing };
        if (!payload.name?.trim()) { toast.error("Name is required"); return; }
        if (!payload.audience_categories?.length) { toast.error("Pick at least one audience category"); return; }
        try {
            if (payload.id && payload.__existing) {
                await axios.put(`${API}/admin/auto-sms/rules/${payload.id}`, payload, { headers: authH });
                toast.success("Rule updated");
            } else {
                await axios.post(`${API}/admin/auto-sms/rules`, payload, { headers: authH });
                toast.success("Rule created");
            }
            setEditing(null);
            await loadAll();
        } catch (e) {
            toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
        }
    };

    const runPreview = async () => {
        if (!editing?.id || !editing?.__existing) {
            toast.error("Save the rule first before previewing audience");
            return;
        }
        setPreview((p) => ({ ...p, busy: true }));
        try {
            const r = await axios.post(
                `${API}/admin/auto-sms/rules/${editing.id}/preview-audience`,
                { home_team: preview.home || null, away_team: preview.away || null },
                { headers: authH },
            );
            setPreview((p) => ({ ...p, count: r.data.phone_count, busy: false }));
        } catch (e) {
            setPreview((p) => ({ ...p, busy: false }));
            toast.error("Preview failed", { description: e?.response?.data?.detail });
        }
    };

    const toggleArrayValue = (arr, val) => (arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);

    const globalOn = !!settings?.enabled;

    return (
        <AdminLayout title="Auto-SMS Engine">
            <div className="space-y-6" data-testid="auto-sms-page">
                {/* Header */}
                <div className="flex items-end justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="font-heading text-3xl font-black text-white flex items-center gap-3">
                            <Zap className="h-7 w-7 text-electric" />
                            Real-Time Auto SMS
                        </h1>
                        <p className="text-sm text-white/50 mt-1 max-w-2xl">
                            Automated Swahili alerts fire during live matches — goals, red cards, penalties, full time — routed to team subscribers, chatters, recent interactors, paying customers, or custom bases.
                        </p>
                    </div>
                    <Button onClick={() => setEditing({ ...emptyRule })} className="bg-electric text-white hover:bg-electric/90" data-testid="new-rule-btn">
                        <Plus className="h-4 w-4 mr-2" /> New Rule
                    </Button>
                </div>

                {/* Global controls */}
                <Card className="bg-paper border-border p-5">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                            <div>
                                <div className="text-[11px] uppercase tracking-[0.18em] text-white/50 font-heading">Master switch</div>
                                <div className="mt-1 text-lg text-white font-bold flex items-center gap-2">
                                    {globalOn ? "LIVE" : "PAUSED"}
                                    <Badge className={globalOn
                                        ? "bg-emerald-400/20 text-emerald-300 border-emerald-400/40 border text-[10px]"
                                        : "bg-orange-400/20 text-orange-300 border-orange-400/40 border text-[10px]"}>
                                        {globalOn ? "Alerts firing" : "No alerts"}
                                    </Badge>
                                </div>
                            </div>
                            <Switch checked={globalOn} onCheckedChange={flipGlobal} data-testid="master-switch" />
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="text-[11px] uppercase tracking-wider text-white/50">Default quota/match</div>
                            <Input
                                type="number"
                                min={1}
                                max={100}
                                defaultValue={settings?.default_quota_per_match || 8}
                                onBlur={(e) => changeDefaultQuota(e.target.value)}
                                className="w-20 bg-obsidian border-border text-white"
                                data-testid="default-quota-input"
                            />
                        </div>
                    </div>
                </Card>

                <Tabs defaultValue="rules">
                    <TabsList className="bg-paper">
                        <TabsTrigger value="rules" data-testid="tab-rules">Rules ({rules.length})</TabsTrigger>
                        <TabsTrigger value="log" data-testid="tab-log">Dispatch Log ({log.length})</TabsTrigger>
                    </TabsList>

                    {/* Rules */}
                    <TabsContent value="rules" className="mt-4">
                        {loading ? (
                            <div className="text-center py-12 text-white/40"><Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading…</div>
                        ) : rules.length === 0 ? (
                            <Card className="bg-paper border-border p-12 text-center text-white/40">
                                No rules yet — create one to start firing real-time alerts.
                            </Card>
                        ) : (
                            <div className="space-y-3" data-testid="rules-list">
                                {rules.map((r) => (
                                    <Card
                                        key={r.id}
                                        className={`bg-paper border-border p-4 ${r.enabled ? "" : "opacity-60"}`}
                                        data-testid={`rule-row-${r.id}`}
                                    >
                                        <div className="flex items-start gap-4">
                                            <Switch
                                                checked={r.enabled}
                                                onCheckedChange={() => toggleRule(r.id)}
                                                data-testid={`toggle-${r.id}`}
                                                className="mt-1"
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <h3 className="font-heading font-bold text-white text-base">{r.name}</h3>
                                                    <Badge variant="outline" className="text-[10px] border-electric/40 text-electric">
                                                        Quota {r.quota_per_match}/match
                                                    </Badge>
                                                    <Badge variant="outline" className="text-[10px] border-white/15 text-white/60">
                                                        Recent {r.recent_days}d
                                                    </Badge>
                                                </div>
                                                <div className="mt-2 flex flex-wrap gap-1.5">
                                                    <span className="text-[10px] text-white/40 uppercase tracking-wider mr-1">Events</span>
                                                    {(r.event_types || []).map((et) => (
                                                        <Badge key={et} className="bg-yellow-300/15 text-yellow-300 border-yellow-300/30 border text-[10px]">{et}</Badge>
                                                    ))}
                                                    {r.event_types?.length === 0 && <span className="text-white/40 text-xs">ALL</span>}
                                                </div>
                                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                                    <span className="text-[10px] text-white/40 uppercase tracking-wider mr-1">Audience</span>
                                                    {(r.audience_categories || []).map((c) => (
                                                        <Badge key={c} className="bg-electric/15 text-electric border-electric/30 border text-[10px]">
                                                            {c.replace(/_/g, " ")}
                                                        </Badge>
                                                    ))}
                                                </div>
                                                {(r.teams?.length || r.competitions?.length) && (
                                                    <div className="mt-1.5 text-[11px] text-white/50">
                                                        {r.teams?.length ? <>Teams: <span className="text-white">{r.teams.join(", ")}</span></> : null}
                                                        {r.teams?.length && r.competitions?.length ? " · " : ""}
                                                        {r.competitions?.length ? <>Leagues: <span className="text-white">{r.competitions.join(", ")}</span></> : null}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-col gap-2">
                                                <Button size="sm" variant="outline" onClick={() => setEditing({ ...r, __existing: true })} className="border-border text-white" data-testid={`edit-${r.id}`}>
                                                    <Edit2 className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button size="sm" variant="outline" onClick={() => deleteRule(r.id)} className="border-orange-400/40 text-orange-300 hover:bg-orange-400/10" data-testid={`delete-${r.id}`}>
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        )}
                    </TabsContent>

                    {/* Dispatch log */}
                    <TabsContent value="log" className="mt-4">
                        {log.length === 0 ? (
                            <Card className="bg-paper border-border p-12 text-center text-white/40">
                                No dispatches in the last 7 days.
                            </Card>
                        ) : (
                            <Card className="bg-paper border-border overflow-hidden">
                                <table className="w-full text-sm">
                                    <thead className="bg-obsidian">
                                        <tr className="text-[10px] uppercase tracking-wider text-white/50 font-heading">
                                            <th className="text-left p-2.5">Sent</th>
                                            <th className="text-left p-2.5">Event</th>
                                            <th className="text-left p-2.5">Recipients</th>
                                            <th className="text-left p-2.5">Message</th>
                                            <th className="text-left p-2.5">Rules fired</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {log.map((r) => (
                                            <tr key={r.id} className="border-t border-border/40 hover:bg-white/[0.02]">
                                                <td className="p-2.5 text-white/70 text-xs">{new Date(r.sent_at).toLocaleString()}</td>
                                                <td className="p-2.5">
                                                    <Badge className="bg-yellow-300/15 text-yellow-300 border-yellow-300/30 border text-[10px]">{r.event_type}</Badge>
                                                </td>
                                                <td className="p-2.5 text-white tabular-nums">{r.phones_count}</td>
                                                <td className="p-2.5 text-white/80 text-xs truncate max-w-[380px]" title={r.message}>{r.message}</td>
                                                <td className="p-2.5 text-white/60 text-xs">{(r.rules_fired || []).length}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Rule editor */}
                <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                    <DialogContent className="bg-paper border-border max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="rule-editor">
                        <DialogHeader>
                            <DialogTitle className="text-white flex items-center gap-2">
                                <Zap className="h-5 w-5 text-electric" />
                                {editing?.__existing ? "Edit rule" : "New auto-SMS rule"}
                            </DialogTitle>
                            <DialogDescription className="text-xs text-white/50">
                                Configure who receives real-time alerts and for which match events.
                            </DialogDescription>
                        </DialogHeader>

                        {editing && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <label className="text-[10px] uppercase tracking-wider text-white/50 font-heading">Name</label>
                                        <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="e.g. Simba engaged fans" className="bg-obsidian border-border text-white" data-testid="rule-name" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider text-white/50 font-heading">Quota / match</label>
                                        <Input type="number" min={1} max={100} value={editing.quota_per_match} onChange={(e) => setEditing({ ...editing, quota_per_match: Number(e.target.value) || 8 })} className="bg-obsidian border-border text-white" data-testid="rule-quota" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider text-white/50 font-heading">Recent window (days)</label>
                                        <Input type="number" min={1} max={365} value={editing.recent_days} onChange={(e) => setEditing({ ...editing, recent_days: Number(e.target.value) || 30 })} className="bg-obsidian border-border text-white" data-testid="rule-recent-days" />
                                    </div>
                                </div>

                                {/* Event types */}
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-white/50 font-heading mb-2 flex items-center gap-1.5">
                                        <Trophy className="h-3 w-3" /> Event types (empty = ALL)
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {cats.event_types.map((et) => (
                                            <label key={et.id} className="flex items-center gap-2 cursor-pointer text-sm text-white/80" data-testid={`event-${et.id}`}>
                                                <Checkbox
                                                    checked={editing.event_types.includes(et.id)}
                                                    onCheckedChange={() => setEditing({ ...editing, event_types: toggleArrayValue(editing.event_types, et.id) })}
                                                />
                                                {et.label}
                                                <span className="text-[10px] text-white/40">({et.swahili})</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Audience categories */}
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-white/50 font-heading mb-2 flex items-center gap-1.5">
                                        <Users className="h-3 w-3" /> Audience categories (union)
                                    </div>
                                    <div className="space-y-2">
                                        {cats.audience_categories.map((ac) => (
                                            <label key={ac.id} className="flex items-start gap-2 cursor-pointer p-2 rounded border border-border hover:border-electric/40" data-testid={`audience-${ac.id}`}>
                                                <Checkbox
                                                    checked={editing.audience_categories.includes(ac.id)}
                                                    onCheckedChange={() => setEditing({ ...editing, audience_categories: toggleArrayValue(editing.audience_categories, ac.id) })}
                                                    className="mt-0.5"
                                                />
                                                <div className="flex-1">
                                                    <div className="text-sm text-white font-medium">{ac.label}</div>
                                                    <div className="text-xs text-white/50">{ac.description}</div>
                                                </div>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Team / competition filter */}
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider text-white/50 font-heading">Teams filter (comma separated · empty = all)</label>
                                        <Input value={(editing.teams || []).join(", ")} onChange={(e) => setEditing({ ...editing, teams: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="Arsenal, Simba SC" className="bg-obsidian border-border text-white" data-testid="rule-teams" />
                                    </div>
                                    <div>
                                        <label className="text-[10px] uppercase tracking-wider text-white/50 font-heading">Competitions filter</label>
                                        <Input value={(editing.competitions || []).join(", ")} onChange={(e) => setEditing({ ...editing, competitions: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} placeholder="eng.1, uefa.champions" className="bg-obsidian border-border text-white" data-testid="rule-competitions" />
                                    </div>
                                </div>

                                {/* Audience preview */}
                                {editing.__existing && (
                                    <Card className="bg-obsidian border-electric/40 p-3">
                                        <div className="text-[10px] uppercase tracking-wider text-white/50 font-heading mb-2">Preview audience</div>
                                        <div className="flex gap-2 flex-wrap">
                                            <Input value={preview.home} onChange={(e) => setPreview({ ...preview, home: e.target.value, count: null })} placeholder="Home team (Arsenal)" className="bg-paper border-border text-white text-sm flex-1 min-w-[140px]" data-testid="preview-home" />
                                            <Input value={preview.away} onChange={(e) => setPreview({ ...preview, away: e.target.value, count: null })} placeholder="Away team (PSG)" className="bg-paper border-border text-white text-sm flex-1 min-w-[140px]" data-testid="preview-away" />
                                            <Button size="sm" onClick={runPreview} disabled={preview.busy} className="bg-electric text-white" data-testid="preview-go">
                                                {preview.busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
                                                Estimate
                                            </Button>
                                        </div>
                                        {preview.count !== null && (
                                            <div className="mt-2 text-sm text-white">
                                                <span className="text-2xl font-heading font-black text-electric tabular-nums">{preview.count}</span>
                                                <span className="text-white/60 text-xs ml-2">phones would receive this alert</span>
                                            </div>
                                        )}
                                    </Card>
                                )}
                            </div>
                        )}

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setEditing(null)} className="border-border text-white">Cancel</Button>
                            <Button onClick={saveRule} className="bg-emerald-400 text-obsidian hover:bg-emerald-300 font-bold" data-testid="save-rule">
                                {editing?.__existing ? "Save changes" : "Create rule"}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default AutoSms;
